/**
 * 「当前正在编辑的章节被删掉之后，编辑器该切到哪一章」——纯函数。
 *
 * ── 为什么要有它（用户实测的问题）────────────────────────────────
 * AI 通过 `execute(target="chapter", action="delete")` 删掉**当前打开的那一章**后：
 * 章节列表刷新了（`loadVolumes` 重新拉到了新的章节树），但 `selectedChapter`
 * 仍指向那个已经不存在的章节对象，`chapterContent` 也还是它的正文 ——
 * 于是编辑器里留着一份**幽灵正文**（标题同样是旧的）。
 *
 * 删完该选谁这件事本身与 React 无关（只是"在一个有序列表里找一个位置的邻居"），
 * 所以抽成纯函数：接线处只负责"算出 id → 切过去"，边界（跨卷、批量删除、删空）
 * 由这里的单测锁住。
 *
 * ── 算法（以**删除前**的顺序为基准）────────────────────────────
 * 1. 当前章节没被删 → 原样返回（调用方据此可以无脑跳过切换）；
 * 2. 往前找**第一个存活者**（跨卷也成立：顺序列表已按「卷序 → 章号」压平）；
 * 3. 前面一个都不剩（删的是第一章）→ 往后找第一个存活者；
 * 4. 一个存活者都没有（整本书被删空）→ 返回 `null`，由调用方给出空状态。
 *
 * 注意第 2 步是"逐个往前跳过被删的章节"，所以**一次删多章**（含当前章）时
 * 落到的是"被删区间之前的最后一个未被删章节"，正是期望行为。
 */

/** 有序章节列表里的一项：只用到 id（顺序由调用方保证） */
export type ChapterIdRef = string | { id: string };

/** 只需要 id / 章节号 两个字段（结构类型，便于单测直接构造） */
export interface FlatChapterLike {
  id: string;
  chapter_number: number;
}

/** 只需要 id / 卷序 两个字段 */
export interface FlatVolumeLike {
  id: string;
  order_index: number;
}

function toId(ref: ChapterIdRef): string {
  return typeof ref === 'string' ? ref : ref.id;
}

/**
 * 把（卷序 → 章号）的章节树压平成**有序 id 列表**。
 *
 * 章节的展示顺序是「卷按 order_index → 卷内按 chapter_number」，而 `chaptersMap`
 * 是一个对象映射、卷数组也不保证有序，所以每次用之前都要显式排序 ——
 * 否则"前一章"会算成任意一章（跨卷尤其明显）。
 */
export function flattenOrderedChapterIds(
  volumes: readonly FlatVolumeLike[],
  chaptersMap: Readonly<Record<string, readonly FlatChapterLike[]>>,
): string[] {
  const sortedVolumes = [...volumes].sort((a, b) => a.order_index - b.order_index);
  const ids: string[] = [];
  for (const vol of sortedVolumes) {
    const chapters = [...(chaptersMap[vol.id] ?? [])].sort((a, b) => a.chapter_number - b.chapter_number);
    for (const chapter of chapters) ids.push(chapter.id);
  }
  return ids;
}

/**
 * 删除后应该选中的章节 id（没有可选的返回 `null`）。
 *
 * @param orderedChaptersBefore 删除前的有序章节列表（`flattenOrderedChapterIds` 的产物，
 *                              或任何"顺序正确"的 id 列表）
 * @param deletedChapterIds     本次被删掉的章节 id（集合或数组都可以）
 * @param currentChapterId      删除前正打开着的章节 id
 */
export function pickChapterAfterDelete(
  orderedChaptersBefore: readonly ChapterIdRef[],
  deletedChapterIds: ReadonlySet<string> | readonly string[],
  currentChapterId: string | null | undefined,
): string | null {
  const order = orderedChaptersBefore.map(toId);
  const deleted = new Set<string>(deletedChapterIds);
  const current = currentChapterId ?? null;

  // 没有当前章节：调用方本来就不需要切换（也不该由这里挑一章）
  if (!current) return null;

  // 当前章节还活着：不动它（这个函数只在"当前章节被删了"时才有意义）
  if (!deleted.has(current)) return current;

  const index = order.indexOf(current);
  if (index < 0) {
    // 数据不一致：当前章节被标记为删除，却不在"删除前列表"里，位置无从判断。
    // 与其把编辑器清空（用户会以为整本书都没了），退化为第一个存活者。
    return order.find(id => !deleted.has(id)) ?? null;
  }

  // 1) 前一个存活者（跨卷自然成立：order 已经是全书的线性顺序）
  for (let i = index - 1; i >= 0; i--) {
    const id = order[i];
    if (!deleted.has(id)) return id;
  }

  // 2) 前面没有存活者（删的是第一章）→ 后一个存活者
  for (let i = index + 1; i < order.length; i++) {
    const id = order[i];
    if (!deleted.has(id)) return id;
  }

  // 3) 全书没有存活章节
  return null;
}
